import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/core'
import type * as Y from 'yjs'
import {
  DEFAULT_LINE_HEIGHT,
  fontStack,
  type BoardTheme,
  type FontDto,
  type RichDoc,
  type TextElement,
} from '@praesto/shared'
import { useKeepInView } from '../components/keepInView.js'
import { useText } from '../i18n.js'
import type { CameraStore } from './CameraStore.js'
import { RichText } from './richText.js'
import { TextSettings } from './TextSettings.js'

/** Nachgeladen, sobald zum ersten Mal getippt wird — siehe `richFragment`. */
const RichEditor = lazy(() =>
  import('./RichEditor.js').then((modul) => ({ default: modul.RichEditor })),
)

interface Props {
  element: TextElement
  selected: boolean
  editable: boolean
  theme: BoardTheme
  /** Selbst gehostete Schriften der Organisation; nur für die Leiste. */
  fonts: readonly FontDto[]
  camera: CameraStore
  onChange: (patch: Partial<TextElement>) => void
  /** Meldet die gemessene Texthöhe, damit das Element mitwächst. */
  onMeasured: (height: number) => void
  /**
   * Besorgt das Yjs-Fragment für die Bearbeitung — erst beim ersten Mal, damit
   * ein Board voller Texte nicht lauter Fragmente anlegt, die niemand braucht.
   */
  onEditFragment: () => Promise<Y.XmlFragment | null>
  /** Nimmt das Feld beim Ziehen an einer Ecke die Schriftgröße mit? */
  scaleFont: boolean
  onToggleScaleFont?: () => void
}

/**
 * Text auf dem Board.
 *
 * Die Schriftgröße steht in Board-Einheiten: Text wächst beim Zoomen mit, wie
 * ein Bild. Bearbeitet wird direkt an Ort und Stelle; Anzeige und Bearbeitung
 * teilen sich Klasse und Schriftmaße, damit beim Umschalten nichts springt.
 *
 * Die Auszeichnung liegt im Element (`rich`), die Bearbeitung läuft über ein
 * Yjs-Fragment. Warum beides, steht in `RichEditor`. Was die Leiste darüber
 * kann, steht in `TextSettings`.
 */
export function TextBody({
  element,
  selected,
  editable,
  theme,
  fonts,
  camera,
  onChange,
  onMeasured,
  onEditFragment,
  scaleFont,
  onToggleScaleFont,
}: Props): React.ReactElement {
  const t = useText()
  // Ein frisch eingefügtes Element ist leer und geht sofort in die Bearbeitung.
  const [editing, setEditing] = useState(() => editable && element.text === '')
  const [fragment, setFragment] = useState<Y.XmlFragment | null>(null)
  const [editor, setEditor] = useState<Editor | null>(null)
  /** Zählt Editor-Schritte hoch, damit die Leiste den Zustand der Marken zeigt. */
  const [, setTakt] = useState(0)
  const bodyRef = useRef<HTMLDivElement>(null)
  // Die Leiste sitzt über dem Text und rückt herunter, wenn oben kein Platz ist.
  const settingsRef = useKeepInView<HTMLDivElement>(camera)

  useEffect(() => {
    if (!editable) setEditing(false)
  }, [editable])

  // Das Fragment wird erst geholt, wenn wirklich bearbeitet wird.
  useEffect(() => {
    if (!editing) {
      setFragment(null)
      return
    }
    let verworfen = false
    void onEditFragment().then((geholt) => {
      if (!verworfen) setFragment(geholt)
    })
    return () => {
      verworfen = true
    }
  }, [editing, onEditFragment])

  /*
   * Was das Bearbeiten beendet: ein Klick irgendwo sonst.
   *
   * Nicht der Fokusverlust des Editors — den löst schon der Griff zum
   * Farbwähler in der eigenen Leiste aus, und dann verlöre man mitten im
   * Färben die Markierung, auf die man gerade zielt.
   */
  useEffect(() => {
    if (!editing) return
    const beiZeiger = (event: PointerEvent): void => {
      const ziel = event.target as Node
      if (bodyRef.current?.contains(ziel) || settingsRef.current?.contains(ziel)) return
      setEditing(false)
    }
    window.addEventListener('pointerdown', beiZeiger, true)
    return () => window.removeEventListener('pointerdown', beiZeiger, true)
  }, [editing, settingsRef])

  useEffect(() => {
    if (!editor) return
    const melden = (): void => setTakt((wert) => wert + 1)
    editor.on('transaction', melden)
    editor.on('selectionUpdate', melden)
    return () => {
      editor.off('transaction', melden)
      editor.off('selectionUpdate', melden)
    }
  }, [editor])

  /*
   * Die Höhe folgt dem Inhalt — solange niemand sie selbst gezogen hat.
   *
   * Gemessen wird auch beim Tippen: Ein Feld, das erst beim Verlassen wächst,
   * schneidet einem beim Schreiben die eigenen Zeilen ab.
   */
  useLayoutEffect(() => {
    if (!element.autoHeight) return
    const node = bodyRef.current
    if (!node) return
    const height = node.scrollHeight
    if (height > 0 && Math.abs(height - element.h) > 1) onMeasured(height)
  })

  const style: React.CSSProperties = {
    fontSize: `${element.fontSize}px`,
    lineHeight: element.lineHeight ?? DEFAULT_LINE_HEIGHT,
    color: element.color,
    textAlign: element.align,
    fontWeight: element.bold ? 600 : 400,
    /*
     * Ohne eigene Wahl gilt die Schrift des Boards; `serif` heißt dabei
     * „Überschriftenschrift", welche das ist, legt das Board-Design fest. Eine
     * ausdrücklich gewählte Schrift schlägt beides.
     */
    fontFamily: fontStack(element.font || (element.serif ? theme.headingFont : theme.bodyFont)),
  }

  const koerperKlasse = [
    'media-text',
    element.autoHeight ? '' : `is-fixed is-${element.valign}`,
    !element.rich && !element.text ? 'is-empty' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {editing && fragment ? (
        <Suspense fallback={null}>
          <RichEditor
            fragment={fragment}
            hostRef={bodyRef}
            className={`${koerperKlasse} is-editing`}
            style={style}
            onChange={(rich: RichDoc, text: string) => onChange({ rich, text })}
            onDone={() => setEditing(false)}
            onReady={setEditor}
            /*
             * Beim Schreiben gehört der Zeiger dem Text.
             *
             * Ohne das griffe der Zug im Text auf das umgebende Element durch:
             * Wer eine Stelle markieren wollte, verschöbe stattdessen das ganze
             * Feld — und käme an keine Markierung heran, auf die die Leiste
             * wirken könnte.
             */
            onPointerDown={(event) => event.stopPropagation()}
          />
        </Suspense>
      ) : (
        <div
          ref={bodyRef}
          className={koerperKlasse}
          style={style}
          onDoubleClick={(event) => {
            if (!editable) return
            event.stopPropagation()
            setEditing(true)
          }}
        >
          {element.rich ? (
            <RichText doc={element.rich} />
          ) : (
            element.text || t('text.placeholder')
          )}
        </div>
      )}

      {selected && editable && (
        <TextSettings
          element={element}
          theme={theme}
          fonts={fonts}
          camera={camera}
          editor={editor}
          onChange={onChange}
          barRef={settingsRef}
          editing={editing}
          scaleFont={scaleFont}
          onToggleScaleFont={onToggleScaleFont}
        />
      )}
    </>
  )
}
